import {
  type IFlagsmithFeature,
  useFlags,
  useFlagsmith,
  useFlagsmithLoading,
} from '@flagsmith/flagsmith/react';
import { useCustomCompareMemo } from '@react-hookz/web';
import { enumValues } from '@utils/enumValues';
import { useEffect } from 'react';
import type { ActiveOrganizationProps } from '../providers/userContextProvider';

const sameStringMembers = (a: readonly string[], b: readonly string[]) => {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  const other = new Set(b);
  return a.every((item) => other.has(item));
};

export enum FlagsmithFlags {
  workflow_review = 'workflow_review',
  auto_invite = 'auto_invite',
  repcode_access = 'repcode_access',
  configurable_table_pages = 'configurable_table_pages',
  mackenzie_account_page = 'mackenzie_account_page',
  conditional_account_page_configuration = 'conditional_account_page_configuration',
  acb_rework = 'acb_rework',
  spanish_language_support = 'spanish_language_support',
  weekdays_only_calendar = 'weekdays_only_calendar',
  text_variants = 'text_variants',
  form_mapper = 'form_mapper',
  show_account_type = 'show_account_type',
  portfolio_trading_prototype = 'portfolio_trading_prototype',
  widgets_additional_info = 'widgets_additional_info',
  close_sub_account = 'close_sub_account',
  alert_banners = 'alert_banners',
  asset_class_portfolios = 'asset_class_portfolios',
  audit_log_widget_v2 = 'audit_log_widget_v2',
  bulk_trader_revamp = 'bulk_trader_revamp',
  bulk_rebalance = 'bulk_rebalance',
  docusign_in_app_signing = 'docusign_in_app_signing',
  recon_flags_dashboard = 'recon_flags_dashboard',
  recon_activities_dashboard = 'recon_activities_dashboard',
  recon_tolerance_config = 'recon_tolerance_config',
  household_portfolio = 'household_portfolio',
  household_portfolio_allocation_methodology = 'household_portfolio_allocation_methodology',
  household_model_portfolio_widget = 'household_model_portfolio_widget',
  household_portfolio_customized_allocations_workflow = 'household_portfolio_customized_allocations_workflow',
  forms_idempotent_form_steps = 'forms_idempotent_form_steps',
  form_completion_reupload_overwrite = 'form_completion_reupload_overwrite',
  use_fee_enable_toggle = 'use_fee_enable_toggle',
  home_page_product_aum_exposure = 'home_page_product_aum_exposure',
  use_multi_billing_cycle_export = 'use_multi_billing_cycle_export',
  tcp_spouse_lookup = 'tcp_spouse_lookup',
  workflow_household_branch_conditions = 'workflow_household_branch_conditions',
  resp_transferred_from_another_institution = 'resp_transferred_from_another_institution',
  preview_pdf_forms = 'preview_pdf_forms',
  mackenzie_release_3 = 'mackenzie_release_3',
  siem_data_feed = 'siem_data_feed',
  advisor_contact_repcode = 'advisor_contact_repcode',
  withdrawal_net_gross = 'withdrawal_net_gross',
}

export type FlagsmithFlagsState = {
  featureFlags: Set<FlagsmithFlags>;
  /**
   * Whether `featureFlags` describes THIS organization yet.
   *
   * Flags are fetched twice on a cold load: once anonymously against the
   * environment, then again for `org:<id>` once the identify effect below has
   * run. Overrides live on the identity, so between those two fetches every
   * per-organization flag reads as its environment default — false. A consumer
   * that merely renders less is fine with that; one that acts irreversibly on a
   * disabled flag is not, and route guards redirected away from pages the
   * organization was entitled to.
   */
  featureFlagsResolved: boolean;
};

export const useFlagsmithFlags = (
  organization: ActiveOrganizationProps,
): FlagsmithFlagsState => {
  const flagsmithFlags = useFlags(enumValues(FlagsmithFlags));
  const flagsmith = useFlagsmith();
  const loadingState = useFlagsmithLoading();
  // Use publicParents instead of parents to get the full inheritance chain
  // publicParents provides public info about parent organizations going up
  // the full inheritance chain to the top level, regardless of user permissions
  const organizationParentsTrait = (organization.publicParents || [])
    .map((p) => p.id)
    .join('/');
  const identity = organization.id ? `org:${organization.id}` : null;
  useEffect(() => {
    if (!identity) return;
    void flagsmith.identify(identity, {
      name: organization.translatedName?.en || null,
      parents: organizationParentsTrait,
    });
  }, [
    identity,
    organizationParentsTrait,
    organization.translatedName?.en,
    flagsmith,
  ]);

  const enabledFlags = Object.entries(flagsmithFlags)
    .filter(([, state]) => (state as IFlagsmithFeature).enabled)
    .map(([key]) => key);
  const featureFlags = useCustomCompareMemo(
    () => new Set(enabledFlags as FlagsmithFlags[]),
    [enabledFlags],
    (prev, next) => sameStringMembers(prev[0], next[0]),
  );

  // Both halves are required. `identify` assigns `flagsmith.identity`
  // synchronously and only then starts the fetch, so the identity alone
  // reports ready while the anonymous flags are still in memory; `isFetching`
  // alone is false during the anonymous fetch's own settled window, before
  // this organization has been asked for at all.
  //
  // A FAILED fetch counts as resolved. The SDK clears isFetching and leaves
  // the anonymous flags in place, so this reports ready over flags that are
  // not this organization's — deliberately. Requiring `error == null` instead
  // would leave every flag-gated route spinning for the whole of a Flagsmith
  // outage, with nothing left running to end it; resolving degrades to the
  // pre-existing behaviour for the outage only.
  const featureFlagsResolved =
    identity != null &&
    flagsmith.identity === identity &&
    loadingState != null &&
    !loadingState.isFetching;

  return { featureFlags, featureFlagsResolved };
};
